import { Injectable } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { map, BehaviorSubject, Observable, switchMap, of, take } from 'rxjs';

import {
  WalletType,
  ReputationType,
  WidgetDataType,
  PointOfSaleType,
  WidgetDataInput,
  GetWidgetDataGQL,
  WidgetVisualsType,
  TranslationAppEnum,
  LoyaltySettingsType,
  GameIntegrationType,
  GetWidgetForTargetGQL,
  WidgetIntegrationType,
  GetWidgetForTargetType,
  GameRewardWithGameType,
  WalletWithReputationDtoType,
  ReputationWithoutTargetType,
  GetReputationsByTargetCacheGQL,
  GetUserWalletWithReputationsGQL,
  CurrentUserReputationsLossDateType,
  PartnershipNetworkWithReferralType,
  FindLoyaltySettingsByTargetCacheGQL,
  PartnershipNetworkWithAccelerateType,
  GetCurrentUserQuantitativeWalletsGQL,
  GetCurrentUserReputationsLossDateGQL,
  FindTargetPartnersWithReferralSettingsGQL,
  FindTargetPartnersWithAccelerateSettingsGQL,
} from '@sifca-monorepo/widget-generator';
import { MobileGamesService, WidgetResolvedGame } from './games.service';
import { MobileQuestsService } from './quests.service';
import { MobilePartnerService } from './partner.service';
import { MobileProfileService } from './profile.service';
import { MobileCardService } from './card.service';
import { filter } from 'lodash';
import { GetWidgetIntegrationByTargetGQL } from '@sifca-monorepo/terminal-generator';

@Injectable({
  providedIn: 'root',
})
export class MobileReputationService {
  private referralCount: BehaviorSubject<number> = new BehaviorSubject(0);
  private levelColor: BehaviorSubject<string> = new BehaviorSubject('');
  private accelerateCount: BehaviorSubject<number> = new BehaviorSubject(0);
  private remainingPoints: BehaviorSubject<number> = new BehaviorSubject(0);
  private pos: BehaviorSubject<PointOfSaleType> = new BehaviorSubject(null);
  private isLastPartner: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private isFinalLevel: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private loadingLoyalty: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private reputationOpened: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private currentLevelPercentage: BehaviorSubject<number> = new BehaviorSubject(0);
  private reputations: BehaviorSubject<ReputationType[]> = new BehaviorSubject(null);
  private quantitativeWallet: BehaviorSubject<WalletType> = new BehaviorSubject(null);
  private widgetVisuals: BehaviorSubject<WidgetVisualsType[]> = new BehaviorSubject(null);
  private wallet: BehaviorSubject<WalletWithReputationDtoType> = new BehaviorSubject(null);
  private loyaltySettings: BehaviorSubject<LoyaltySettingsType> = new BehaviorSubject(null);
  private aggregatorSettings: BehaviorSubject<LoyaltySettingsType> = new BehaviorSubject(null);
  private selectedSettings: BehaviorSubject<LoyaltySettingsType> = new BehaviorSubject(null);
  private widgetSettings: BehaviorSubject<WidgetIntegrationType> = new BehaviorSubject(null);
  private nextLevel: BehaviorSubject<ReputationWithoutTargetType> = new BehaviorSubject(null);
  private currentLevel: BehaviorSubject<ReputationWithoutTargetType> = new BehaviorSubject(null);
  private partners: BehaviorSubject<PartnershipNetworkWithAccelerateType[]> = new BehaviorSubject(null);
  private selectedReputationWallet: BehaviorSubject<WalletWithReputationDtoType> = new BehaviorSubject(null);
  private frequencyLossDate: BehaviorSubject<CurrentUserReputationsLossDateType> = new BehaviorSubject(null);

  pageLimit = 4;
  partnerPageIndex = 0;

  get accelerateCount$(): Observable<number> {
    return this.accelerateCount.asObservable();
  }

  get referralCount$(): Observable<number> {
    return this.referralCount.asObservable();
  }

  get reputations$(): Observable<ReputationType[]> {
    return this.reputations.asObservable();
  }

  get nextLevel$(): Observable<ReputationWithoutTargetType> {
    return this.nextLevel.asObservable();
  }
  set nextLevel$(value: any) {
    this.nextLevel.next(value);
  }

  get isFinalLevel$(): Observable<boolean> {
    return this.isFinalLevel.asObservable();
  }
  set isFinalLevel$(value: any) {
    this.isFinalLevel.next(value);
  }

  get currentLevel$(): Observable<ReputationWithoutTargetType> {
    return this.currentLevel.asObservable();
  }
  set currentLevel$(value: any) {
    this.currentLevel.next(value);
  }

  get remainingPoints$(): Observable<number> {
    return this.remainingPoints.asObservable();
  }
  set remainingPoints$(value: any) {
    this.remainingPoints.next(value);
  }
  get currentLevelPercentage$(): Observable<number> {
    return this.currentLevelPercentage.asObservable();
  }
  set currentLevelPercentage$(value: any) {
    this.currentLevelPercentage.next(value);
  }

  get levelColor$(): Observable<string> {
    return this.levelColor.asObservable();
  }
  set levelColor$(value: any) {
    this.levelColor.next(value);
  }

  get frequencyLossDate$(): Observable<CurrentUserReputationsLossDateType> {
    return this.frequencyLossDate.asObservable();
  }
  get quantitativeWallet$(): Observable<WalletType> {
    return this.quantitativeWallet.asObservable();
  }
  set quantitativeWallet$(value: any) {
    this.quantitativeWallet.next(value);
  }

  get widgetSettings$(): Observable<WidgetIntegrationType> {
    return this.widgetSettings.asObservable();
  }
  set widgetSettings$(value: any) {
    this.widgetSettings.next(value);
  }

  get pos$(): Observable<PointOfSaleType> {
    return this.pos.asObservable();
  }
  set pos$(value: any) {
    this.pos.next(value);
  }

  get selectedSettings$(): Observable<LoyaltySettingsType> {
    return this.selectedSettings.asObservable();
  }
  set selectedSettings$(value: any) {
    this.selectedSettings.next(value);
  }

  get loyaltySettings$(): Observable<LoyaltySettingsType> {
    return this.loyaltySettings.asObservable();
  }
  set loyaltySettings$(value: any) {
    this.loyaltySettings.next(value);
  }

  get aggregatorSettings$(): Observable<LoyaltySettingsType> {
    return this.aggregatorSettings.asObservable();
  }
  set aggregatorSettings$(value: any) {
    this.aggregatorSettings.next(value);
  }

  get partners$(): Observable<PartnershipNetworkWithAccelerateType[]> {
    return this.partners.asObservable();
  }
  set partners$(value: any) {
    this.partners.next(value);
  }

  get selectedReputationWallet$(): Observable<WalletWithReputationDtoType> {
    return this.selectedReputationWallet.asObservable();
  }
  set selectedReputationWallet$(value: any) {
    this.selectedReputationWallet.next(value);
  }

  get wallet$(): Observable<WalletWithReputationDtoType> {
    return this.wallet.asObservable();
  }
  set wallet$(value: any) {
    this.wallet.next(value);
  }

  get reputationOpened$(): Observable<boolean> {
    return this.reputationOpened.asObservable();
  }
  set reputationOpened$(value: any) {
    this.reputationOpened.next(value);
  }

  get isLastPartner$(): Observable<boolean> {
    return this.isLastPartner.asObservable();
  }

  get widgetVisuals$(): Observable<WidgetVisualsType[]> {
    return this.widgetVisuals.asObservable();
  }

  constructor(
    private cardService: MobileCardService,
    private gamesService: MobileGamesService,
    private questService: MobileQuestsService,
    private getWidgetDataGQL: GetWidgetDataGQL,
    private profileService: MobileProfileService,
    private partnerService: MobilePartnerService,
    private getWidgetForTargetGQL: GetWidgetForTargetGQL,
    private getReputationsByTargetGQL: GetReputationsByTargetCacheGQL,
    private getWidgetIntegrationByTargetGQL: GetWidgetIntegrationByTargetGQL,
    private getUserWalletWithReputationsGQL: GetUserWalletWithReputationsGQL,
    private findLoyaltySettingsByTargetCacheGQL: FindLoyaltySettingsByTargetCacheGQL,
    private getCurrentUserQuantitativeWalletsGQL: GetCurrentUserQuantitativeWalletsGQL,
    private getCurrentUserReputationsLossDateGQL: GetCurrentUserReputationsLossDateGQL,
    private findTargetPartnersWithReferralSettingsGQL: FindTargetPartnersWithReferralSettingsGQL,
    private findTargetPartnersWithAccelerateSettingsGQL: FindTargetPartnersWithAccelerateSettingsGQL,
  ) {}

  getWidgetData(input?: WidgetDataInput): Observable<WidgetDataType> {
    this.questService.loadingQuests$ = true;
    return this.getWidgetDataGQL
    .fetch({
        input: {
          ...input,
          questPagination: { page: 0, limit: 10 },
          freebiePagination: { page: 0, limit: 50 },
          leaderboardPagination: { page: 0, limit: 10 },
          target: { pos: localStorage.getItem('posId') },
        },
      })
      .pipe(
        map(({ data }: any) => {
          const widgetData = data?.getWidgetData;
          const quests = widgetData?.quests?.objects || [];
          this.questService.loadingQuests$ = false;
          this.questService.homeQuests$ = quests;
          this.questService.quests$ = quests;
          this.questService.isLast$ = widgetData?.quests?.isLast;
          this.questService.isLastHomeQuests$ = widgetData?.quests?.isLast;
          this.gamesService.gameWinners$ = widgetData?.gameWinners;
          this.cardService.userCard$ = widgetData?.corporateUserCards?.[0];
          this.quantitativeWallet.next(widgetData?.quantitativeWallets?.objects[0]);
          this.partnerService.freebies$ = widgetData?.freebieTransactions?.objects;
          this.gamesService.weekRewards$ = widgetData?.currentWeekRewards;
          this.gamesService.dayRewards$ = widgetData?.currentDayRewards;
          this.profileService.completeProfile$ = widgetData?.profileCompleteness;
          if (widgetData?.liveTopGamesLeaderboard?.leaderboards?.length) {
            const board = filter(widgetData?.liveTopGamesLeaderboard?.leaderboards, (b: GameRewardWithGameType) => b.game.code != 'SCZ');
            this.gamesService.topLeaderboard$ = board;
          }
          this.gamesService.games$ = this.getGames(widgetData?.gameIntegration, widgetData.aggregatorGameIntegration);
          return widgetData;
        }),
      );
  }
  
  getGames(partnerGameIntegration?: GameIntegrationType, aggregatorGameIntegration?: GameIntegrationType): WidgetResolvedGame[] {
    const gameIntegration = aggregatorGameIntegration || partnerGameIntegration;
    let selectedGames = this.gamesService.normalizeIntegrationGames(gameIntegration);
    selectedGames = filter(selectedGames, (game) => game.code != 'SCZ');
    const gameGroup = gameIntegration?.gameGroups?.[0];
    this.gamesService.gameGroup$ = gameGroup ? { ...gameGroup, games: selectedGames } : null;

    return selectedGames;
  }

  resetPartnerPagination() {
    this.partnerPageIndex = 0;
    this.partners.next([]);
  }

  findTargetPartnersWithAccelerateSettings(): Observable<PartnershipNetworkWithAccelerateType[]> {
    return this.findTargetPartnersWithAccelerateSettingsGQL
      .fetch({ pagination: { page: this.partnerPageIndex, limit: this.pageLimit }, target: { pos: localStorage.getItem('posId') } })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            this.partners.next(data.findTargetPartnersWithAccelerateSettings.objects);
            this.accelerateCount.next(data.findTargetPartnersWithAccelerateSettings.count);
            this.isLastPartner.next(data.findTargetPartnersWithAccelerateSettings.isLast);
            return data.findTargetPartnersWithAccelerateSettings.objects;
          }
        }),
      );
  }

  findTargetPartnersWithReferralSettings(): Observable<PartnershipNetworkWithReferralType[]> {
    return this.findTargetPartnersWithReferralSettingsGQL
      .fetch({ pagination: { page: this.partnerPageIndex, limit: this.pageLimit }, target: { pos: localStorage.getItem('posId') } })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            this.referralCount.next(data.findTargetPartnersWithReferralSettings.count);
            this.isLastPartner.next(data.findTargetPartnersWithReferralSettings.isLast);
            return data.findTargetPartnersWithReferralSettings.objects;
          }
        }),
      );
  }

  findLoyaltySettingsByTargetOrAggregator(partnerPosId?: string): Observable<LoyaltySettingsType> {
    let aggregatorPosId: string;
    this.loadingLoyalty.next(true);
    return this.loyaltySettings$.pipe(
      take(1),
      switchMap((settings) => {
        aggregatorPosId = settings?.aggregator?.target?.pos?.id;
        return this.findLoyaltySettingsByTargetCacheGQL.fetch({
          target: { pos: partnerPosId || aggregatorPosId || localStorage.getItem('posId') },
        });
      }),
      map(({ data }: any) => {
        this.loadingLoyalty.next(false);
        if (data) {
          if (!aggregatorPosId && !partnerPosId) {
            this.pos.next(data.findLoyaltySettingsByTargetCache.target.pos);
            this.loyaltySettings.next(data.findLoyaltySettingsByTargetCache);
          }
          return data.findLoyaltySettingsByTargetCache;
        }
      }),
    );
  }

  getWidgetForTarget(app?: TranslationAppEnum): Observable<GetWidgetForTargetType> {
    return this.getWidgetForTargetGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') },
        app: app ? app : TranslationAppEnum.WIDGET_MOBILE,
        pagination: { page: 0, limit: 100000 },
      })
      .pipe(
        map(({ data }: any) => {
          this.widgetVisuals.next(data.getWidgetForTarget.visuals.objects);
          this.loyaltySettings.next(data.getWidgetForTarget.loyaltySettings);
          this.aggregatorSettings.next(data.getWidgetForTarget.aggregatorLoyaltySettings);
          this.pos.next(data.getWidgetForTarget.loyaltySettings.target.pos);
          this.widgetSettings.next(data.getWidgetForTarget.widgetIntegrationSettings);
          return data.getWidgetForTarget;
        }),
      );
  }

  getWidgetIntegrationByTarget(): Observable<GetWidgetForTargetType> {
    return this.getWidgetIntegrationByTargetGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') }
      })
      .pipe(
        map(({ data }: any) => {
          this.widgetSettings.next(data.getWidgetIntegrationByTarget);
          return data.getWidgetForTarget;
        }),
      );
  }

  getUserWalletWithReputations(): Observable<WalletWithReputationDtoType> {
    return this.getUserWalletWithReputationsGQL
    .fetch({ ...(localStorage.getItem('userToken') ? { userToken: localStorage.getItem('userToken') } : {}) })
    .pipe(
      map(({ data }: any) => {
        if (data) {
          this.wallet.next(data.getUserWalletWithReputations);
            return data.getUserWalletWithReputations;
          }
        }),
      );
  }

  getCurrentUserReputationsLossDate(): Observable<CurrentUserReputationsLossDateType> {
    return this.getCurrentUserReputationsLossDateGQL
      .fetch({ ...(localStorage.getItem('userToken') ? { userToken: localStorage.getItem('userToken') } : {}) })
      .pipe(
        map(({ data }: any) => {
          this.frequencyLossDate.next(data?.getCurrentUserReputationsLossDate);
          return data?.getCurrentUserReputationsLossDate;
        }),
      );
  }

  getReputationsByTarget(fetchPolicy: FetchPolicy = 'cache-first'): Observable<ReputationType[]> {
    return this.getReputationsByTargetGQL.fetch({ target: { pos: localStorage.getItem('posId') } }, { fetchPolicy }).pipe(
      map(({ data }: any) => {
        if (data) {
          this.reputations.next(data.getReputationsByTargetCache);
          return data.getReputationsByTargetCache as any;
        }
      }),
    );
  }

  getCurrentUserQuantitativeWallets(): Observable<WalletType[]> {
    return this.getCurrentUserQuantitativeWalletsGQL.fetch({ pagination: { page: 0, limit: 50 } }).pipe(
      switchMap(({ data }: any) => {
        if (data) {
          this.quantitativeWallet.next(data.getCurrentUserQuantitativeWallets.objects[0]);
          return of(data.getCurrentUserQuantitativeWallets.objects[0]);
        }
        return of(null);
      }),
    );
  }
}
